import { createListCollection } from "@chakra-ui/react";
import * as React from "react";

import {
  SelectContent,
  SelectItem,
  SelectRoot,
  SelectTrigger,
  SelectValueText,
} from "./select";

export const REGION_TIMEZONES = {
  Africa: "Africa/Johannesburg",
  Asia: "Asia/Tokyo",
  Europe: "Europe/Berlin",
  "Middle East": "Asia/Dubai",
  "North America": "America/New_York",
  Oceania: "Australia/Sydney",
  "South America": "America/Sao_Paulo",
} as const;

const TIMEZONE_ALIASES: Record<string, string> = {
  "America/Los_Angeles": "America/New_York",
  "America/Chicago": "America/New_York",
  "Europe/Amsterdam": "Europe/Berlin",
};

interface RegionTimezoneSelectProps {
  value: string;
  onChange: (timezone: string) => void;
  onBlur?: () => void;
  disabled?: boolean;
  size?: "xs" | "sm" | "md" | "lg";
  placeholder?: string;
  includeDefault?: boolean;
  defaultLabel?: string;
}

export default function RegionTimezoneSelect({
  value,
  onChange,
  onBlur,
  disabled,
  size = "sm",
  placeholder,
  includeDefault = false,
  defaultLabel,
}: RegionTimezoneSelectProps) {
  const collection = React.useMemo(() => {
    const items = [];

    if (includeDefault) {
      items.push({
        value: "",
        label: defaultLabel || "Default",
      });
    }

    items.push(
      ...Object.entries(REGION_TIMEZONES).map(([region, timezone]) => ({
        value: timezone,
        label: region,
      })),
    );

    return createListCollection({ items });
  }, [defaultLabel, includeDefault]);

  const normalizedValue = TIMEZONE_ALIASES[value] || value;

  return (
    <SelectRoot
      size={size}
      value={[normalizedValue]}
      onValueChange={({ value }) => onChange(value[0])}
      onBlur={onBlur}
      collection={collection}
      disabled={disabled}
    >
      <SelectTrigger>
        <SelectValueText placeholder={placeholder || "Select a region"} />
      </SelectTrigger>
      <SelectContent>
        {collection.items.map((item) => (
          <SelectItem key={item.value} item={item}>
            {item.label}
          </SelectItem>
        ))}
      </SelectContent>
    </SelectRoot>
  );
}
